import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import { siteConfig } from './config/siteConfig'
import './i18n/index' // Initialize comprehensive i18n
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n/index'

// Global error handling with enhanced accessibility and user experience
const handleGlobalError = (event) => {

  // Show user-friendly error notification instead of breaking the app
  const errorNotification = document.createElement('div');
  errorNotification.innerHTML = `
    <div role="alert" aria-live="assertive" style="
      position: fixed; 
      top: 20px; 
      right: 20px; 
      background: ${siteConfig.colors.warning}; 
      color: white; 
      padding: 16px; 
      border-radius: 8px; 
      font-family: ${siteConfig.fonts.body};
      font-size: 14px;
      z-index: 9999;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      max-width: 300px;
    ">
      <strong>⚠️ Something went wrong</strong><br>
      We're working to fix this. Please try refreshing the page.
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: none; 
        border: none; 
        color: white; 
        float: right; 
        cursor: pointer; 
        font-size: 18px;
        margin-top: -4px;
      ">×</button>
    </div>
  `;
  document.body.appendChild(errorNotification);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (errorNotification.parentElement) {
      errorNotification.parentElement.removeChild(errorNotification);
    }
  }, 10000);
};

// Application initialization

// WCAG 2.1 Level AA compliance check
const performAccessibilityCheck = () => {
  const container = document.getElementById('ffp-react-app');
  if (container && !container.getAttribute('role')) {
    container.setAttribute('role', 'main');
    container.setAttribute('aria-label', 'Flood & Fire Pros Restoration Marketplace');
  }
};

// Find the container and initialize React application
const container = document.getElementById('ffp-react-app');

if (container) {
  // Accessibility enhancements
  performAccessibilityCheck();

  // Create React root
  const root = ReactDOM.createRoot(container);
  root.render(
    <I18nextProvider i18n={i18n}>
      <App />
    </I18nextProvider>
  );

  // Track successful load for analytics
  if (window.gtag) {
    window.gtag('event', 'app_loaded', {
      event_category: 'engagement',
      event_label: 'react_app'
    });
  }
  
} else {

  // Enhanced fallback with brand compliance and accessibility
  document.body.innerHTML = `
    <div role="main" aria-label="Configuration Error" style="
      padding: 40px; 
      text-align: center; 
      font-family: ${siteConfig.fonts.body}; 
      background: ${siteConfig.colors.primary}; 
      color: white; 
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    ">
      <div style="max-width: 500px;">
        <h1 style="
          font-family: ${siteConfig.fonts.headings}; 
          font-size: 2.5rem; 
          margin-bottom: 1rem; 
          color: ${siteConfig.colors.accent};
        ">
          🔧 Configuration Error
        </h1>
        <p style="font-size: 1.1rem; margin-bottom: 2rem; line-height: 1.6;">
          We're unable to load the ${siteConfig.company.name} application. 
          This is likely a temporary technical issue.
        </p>
        <div style="margin-bottom: 2rem;">
          <p style="margin-bottom: 1rem;">
            <strong>Need immediate help with water or fire damage?</strong>
          </p>
          <a href="/request-service" style="
            display: inline-block;
            background: ${siteConfig.colors.accent};
            color: white;
            padding: 12px 24px;
            text-decoration: none;
            border-radius: 8px;
            font-weight: bold;
            font-size: 1.1rem;
          ">
            📞 Request Emergency Service
          </a>
        </div>
        <button onclick="window.location.reload()" style="
          background: transparent; 
          border: 2px solid white; 
          color: white; 
          padding: 10px 20px; 
          border-radius: 6px; 
          cursor: pointer; 
          font-family: ${siteConfig.fonts.body};
        ">
          🔄 Try Again
        </button>
        <p style="font-size: 0.9rem; margin-top: 2rem; opacity: 0.8;">
          Error Code: CONTAINER_NOT_FOUND
        </p>
      </div>
    </div>
  `;
}

// Enhanced global error handling
window.addEventListener('error', handleGlobalError);

// Unhandled promise rejection handling
window.addEventListener('unhandledrejection', (event) => {

  handleGlobalError({ error: event.reason });
});

// Performance monitoring with enhanced metrics
window.addEventListener('load', () => {
  const loadTime = performance.now();
  
  // Performance analytics tracking
  if (window.gtag) {
    window.gtag('event', 'timing_complete', {
      name: 'load',
      value: Math.round(loadTime)
    });
  }
  
  // Check for slow loading and notify development team
  if (loadTime > 3000) {
    
  }
});

// Keyboard accessibility for emergency actions
document.addEventListener('keydown', (event) => {
  // Emergency shortcut: Ctrl/Cmd + E for emergency help
  // Routes through platform for proper tracking (never direct tel:)
  if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
    event.preventDefault();
    window.location.href = '/get-help-now';
  }
});

// Visibility change handling for better UX
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    // Could implement real-time data refresh here
  }
});

// Network status monitoring for offline capability
window.addEventListener('online', () => {
  // Could implement offline queue sync here
});

window.addEventListener('offline', () => {

});

// Export site configuration for use in other modules
window.FFP_CONFIG = siteConfig;